// MÚI GIỜ HIỂN THỊ của HỆ — MỘT nguồn cho cả kernel (cùng khuôn dateOrder.js / taskTimeOrder.js).
//
// Kiểm i18n 12/10/2026: format.js ghim cứng `Asia/Ho_Chi_Minh` làm mặc định cho fmtDate/fmtTime/fmtDateTime/
// fmtWeekday, và utils/dateVN.js `todayVN()` quyết «hôm nay»/«quá hạn». Đúng cho Work + Core (người dùng Việt,
// nghiệp vụ tính theo giờ VN), SAI cho Site Connect: người Toronto thấy tin nhắn 13:00Z in «08:00 p.m.», và
// từ ~13h EDT việc hạn HÔM NAY đã hiện «quá hạn» (VN đã sang ngày mai).
//
// Luật: giờ hiển thị + «hôm nay» theo MÚI GIỜ CỦA WORKSPACE — chính `tenant_settings.company_timezone` mà máy
// chủ Connect dùng để cắt ngày (tenantClockService / zoneResolver.forTenant). FE và máy chủ cùng một ranh giới
// ngày thì «quá hạn», «hôm nay», báo cáo theo ngày mới khớp nhau.
//
// Host khai SAU KHI biết workspace (đăng nhập / đổi workspace / nạp lại phiên): `setDisplayTimezone(tz)` —
// admin Connect đọc `active_tenant.company_timezone`. Không khai ⇒ 'Asia/Ho_Chi_Minh' — Work, Core, console
// y nguyên như trước.
// ⚠️ KHÔNG đọc `@host/tenant` / store phiên ở đây: console của Work + Connect nạp `@cnv/ui/i18n` mà không có
// alias đó, và múi giờ là của WORKSPACE (mỗi workspace một múi) chứ không phải của bản build.

export const DEFAULT_DISPLAY_TIMEZONE = 'Asia/Ho_Chi_Minh';

let mui = DEFAULT_DISPLAY_TIMEZONE;
const listeners = new Set();

/** Tên múi IANA dùng được không — `Intl` ném RangeError với tên lạ; dữ liệu cột có thể là rác. */
export function isValidTimezone(tz) {
  if (typeof tz !== 'string' || !tz.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz.trim() });
    return true;
  } catch {
    return false;
  }
}

/**
 * Đặt múi giờ hiển thị. Rỗng / tên lạ ⇒ về mặc định (giờ VN) — một cột rác không được làm trắng màn hình.
 * Trả về múi THẬT SỰ được áp (để host so xem có đổi không).
 */
export function setDisplayTimezone(tz) {
  const next = isValidTimezone(tz) ? tz.trim() : DEFAULT_DISPLAY_TIMEZONE;
  if (next === mui) return mui;
  mui = next;
  for (const fn of listeners) {
    try { fn(mui); } catch { /* một người nghe hỏng không chặn người khác */ }
  }
  return mui;
}

export function getDisplayTimezone() {
  return mui;
}

/** Nghe lúc múi giờ hiển thị đổi (host dựng lại cây trang). Trả hàm huỷ. */
export function subscribeDisplayTimezone(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ── Phép tính múi giờ thuần (không phụ thuộc múi của MÁY người xem) ─────────────────────────────────

const boDocCache = new Map();
function boDoc(tz) {
  let f = boDocCache.get(tz);
  if (!f) {
    // `numberingSystem: 'latn'` — máy đặt tiếng Ả Rập/Bengali trả chữ số bản địa, parseInt ra NaN (zoneClock.js).
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', numberingSystem: 'latn',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    boDocCache.set(tz, f);
  }
  return f;
}

/** Các phần ngày-giờ (số) của một mốc, đọc theo múi `tz`. */
function phanCua(ms, tz) {
  const p = {};
  for (const x of boDoc(tz).formatToParts(new Date(ms))) {
    if (x.type !== 'literal') p[x.type] = parseInt(x.value, 10);
  }
  return p;
}

/** Lệch (phút) của múi `tz` so với UTC TẠI mốc `ms` — có tính giờ mùa hè. Toronto tháng 10 = −240. */
export function tzOffsetMinutesAt(ms, tz = mui) {
  const p = phanCua(ms, tz);
  const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second);
  return Math.round((wall - Math.floor(ms / 1000) * 1000) / 60000);
}

const pad = (n, w = 2) => String(n).padStart(w, '0');

/** 'YYYY-MM-DD' của một mốc, đọc theo múi `tz` (mặc định: múi hiển thị). */
export function ymdInTimezone(d = new Date(), tz = mui) {
  const ms = d instanceof Date ? d.getTime() : new Date(d).getTime();
  if (Number.isNaN(ms)) return '';
  const p = phanCua(ms, tz);
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}`;
}

/**
 * GIỜ TƯỜNG «YYYY-MM-DD» + «HH:mm» ở múi `tz` → mốc UTC (ms). Dùng cho ô nhập ngày-giờ: người dùng gõ 17:00
 * theo giờ của workspace, gửi máy chủ là UTC.
 *
 * Hai lượt dò lệch vì giờ mùa: lệch tại «giờ tường đọc như UTC» có thể khác lệch tại mốc thật (Toronto ngày
 * 01/11 lùi giờ). Giờ RƠI VÀO KHE nhảy giờ (02:30 sáng ngày tiến giờ — không tồn tại) ra mốc lệch một giờ,
 * như mọi lịch khác.
 */
export function wallTimeToMs(ymd, hm = '00:00', tz = mui) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  if (!m) return NaN;
  const t = /^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?/.exec(String(hm || '00:00')) || [];
  const gia = Date.UTC(+m[1], +m[2] - 1, +m[3], +(t[1] || 0), +(t[2] || 0), +(t[3] || 0), +String(t[4] || 0).padEnd(3, '0'));
  const lech1 = tzOffsetMinutesAt(gia, tz);
  let ms = gia - lech1 * 60000;
  const lech2 = tzOffsetMinutesAt(ms, tz);
  if (lech2 !== lech1) ms = gia - lech2 * 60000;
  return ms;
}

/** Mốc bất kỳ → 'YYYY-MM-DDTHH:mm' giờ tường ở múi `tz` — giá trị cho <input type="datetime-local">. */
export function msToWallTime(d, tz = mui) {
  const ms = d instanceof Date ? d.getTime() : new Date(d).getTime();
  if (Number.isNaN(ms)) return '';
  const p = phanCua(ms, tz);
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour % 24)}:${pad(p.minute)}`;
}
